import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ToastProvider } from "@/lib/toast";
import { ConfirmProvider } from "@/components/ConfirmModal";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import PortalLayout from "@/components/PortalLayout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyDetailPage from "@/pages/CompanyDetailPage";
import ContactsPage from "@/pages/ContactsPage";
import InventoryPage from "@/pages/InventoryPage";
import PipelinePage from "@/pages/PipelinePage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import DocketsPage from "@/pages/DocketsPage";
import DocketPrintPage from "@/pages/DocketPrintPage";
import DeliveryZonesPage from "@/pages/DeliveryZonesPage";
import UsersPage from "@/pages/UsersPage";
import UserManagementPage from "@/pages/UserManagementPage";
import MessagesPage from "@/pages/MessagesPage";
import SettingsPage from "@/pages/SettingsPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import MarketingPage from "@/pages/MarketingPage";
import YardStockUpdatePage from "@/pages/YardStockUpdatePage";
import InvoicingPage from "@/pages/InvoicingPage";
import PortalOverviewPage from "@/pages/PortalOverviewPage";
import PortalInventoryPage from "@/pages/PortalInventoryPage";
import PortalOrdersPage from "@/pages/PortalOrdersPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import UserManualPage from "@/pages/UserManualPage";
import ProfilePage from "@/pages/ProfilePage";

function LoginGate() {
  const { user } = useAuth();
  if (user && user !== false) {
    const dest = ["reseller", "admin_reseller"].includes(user.role) ? "/portal" : "/";
    return <Navigate to={dest} replace />;
  }
  return <LoginPage />;
}

function RoleHome() {
  const { user } = useAuth();
  if (["reseller", "admin_reseller"].includes(user.role)) return <Navigate to="/portal" replace />;
  return <DashboardPage />;
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />

            {/* Reseller portal */}
            <Route element={<ProtectedRoute roles={["reseller", "admin_reseller", "admin", "user"]}><PortalLayout /></ProtectedRoute>}>
              <Route path="portal" element={<PortalOverviewPage />} />
              <Route path="portal/inventory" element={<PortalInventoryPage />} />
              <Route path="portal/orders" element={<PortalOrdersPage />} />
            </Route>

            {/* Print views — outside AppLayout for clean print output */}
            <Route element={<ProtectedRoute><DocketPrintPage /></ProtectedRoute>} path="dockets/:id/print" />

            {/* User manual — accessible to any authenticated user, no chrome around it */}
            <Route path="manual" element={<ProtectedRoute><UserManualPage /></ProtectedRoute>} />

            {/* Yard Tablet — full-screen, no sidebar, big-touch UI */}
            <Route
              path="yard/stock-update"
              element={<ProtectedRoute roles={["admin", "user", "warehouse"]}><YardStockUpdatePage /></ProtectedRoute>}
            />

            {/* Internal staff app */}
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<RoleHome />} />
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="companies/:id" element={<CompanyDetailPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="pipeline" element={<ProtectedRoute roles={["admin", "user", "admin_reseller"]}><PipelinePage /></ProtectedRoute>} />
              <Route path="orders/:id" element={<ProtectedRoute roles={["admin", "user", "admin_reseller"]}><OrderDetailPage /></ProtectedRoute>} />
              <Route path="dockets" element={<ProtectedRoute roles={["admin", "user", "warehouse"]}><DocketsPage /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute roles={["admin"]}><UsersPage /></ProtectedRoute>} />
              <Route path="settings/user-management" element={<ProtectedRoute roles={["admin"]}><UserManagementPage /></ProtectedRoute>} />
              <Route path="messages" element={<ProtectedRoute roles={["admin", "user", "admin_reseller", "warehouse"]}><MessagesPage /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute roles={["admin"]}><SettingsPage /></ProtectedRoute>} />
              <Route path="settings/delivery-zones" element={<ProtectedRoute roles={["admin"]}><DeliveryZonesPage /></ProtectedRoute>} />
              <Route path="purchase-orders" element={<ProtectedRoute roles={["admin", "user"]}><PurchaseOrdersPage /></ProtectedRoute>} />
              <Route path="invoicing" element={<ProtectedRoute roles={["admin"]}><InvoicingPage /></ProtectedRoute>} />
              <Route path="marketing" element={<ProtectedRoute roles={["admin"]}><MarketingPage /></ProtectedRoute>} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </ConfirmProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
